import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { Link } from 'react-router-dom';
import { useAlert } from '../../constants/AlertProvider';
import { getUserDetails } from '../../utils/getUserDetails';
import { SkeletonLoader } from '../common/SkeletonLoader';
import RecHomeNavbar from "./RecHomeNavbar";
import { BriefcaseIcon, PercentIcon, Cog } from 'lucide-react';

export default function CandidateMatches() {
    const [jobs, setJobs] = useState([]);
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const showAlert = useAlert();

    useEffect(() => {
        const fetchJobsAndMatches = async () => {
            setLoading(true);
            try {
                const userDetails = await getUserDetails();
                if (!userDetails?.company) {
                    showAlert("Company details not found for this recruiter.", "warning");
                    setLoading(false);
                    return;
                }

                const csrftoken = Cookies.get("csrftoken");
                const headers = { "X-CSRFToken": csrftoken };

                // 1. Fetch jobs for the recruiter's company
                const jobsResponse = await axios.get(`/api/jobprofile/?company=${userDetails.company}`, { headers });
                const postedJobs = jobsResponse.data.results;
                setJobs(postedJobs);

                if (postedJobs.length === 0) {
                    setLoading(false);
                    return; // No jobs, so no matches to fetch
                }

                // 2. Fetch matches for each job
                const matchPromises = postedJobs.map(job =>
                    axios.get(`/api/match-results/?job=${job.id}`, { headers })
                );

                const matchResponses = await Promise.all(matchPromises);
                const allMatches = matchResponses.flatMap(res => res.data.results);
                setMatches(allMatches);

            } catch (error) {
                showAlert("Error fetching matches. Please try again later.", "error");
                console.error("Error fetching jobs and matches:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchJobsAndMatches();
    }, [showAlert]);

    if (loading) {
        return (
            <>
                <RecHomeNavbar />
                <div className="mt-14 bg-gray-50 dark:bg-gray-900 min-h-[calc(100vh-56px)] flex flex-col justify-center items-center">
                    <div className="relative flex items-center justify-center">
                        <Cog className="w-20 h-20 text-blue-500 animate-spin" style={{ animationDuration: '3s' }} />
                        <Cog className="w-12 h-12 text-blue-600 absolute animate-spin-reverse" style={{ animationDuration: '2s' }} />
                    </div>
                    <p className="mt-6 text-lg font-semibold text-gray-900 dark:text-gray-100 dark:text-gray-100">
                        Finding your perfect matches...
                    </p>
                </div>
            </>
        );
    }

    if (jobs.length === 0) {
        return (
            <>
                <RecHomeNavbar />
                <div className="mt-14 text-center p-10 bg-gray-50 dark:bg-gray-900 min-h-[calc(100vh-56px)] flex flex-col justify-center items-center">
                    <BriefcaseIcon className="w-16 h-16 text-gray-400 mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">No Jobs Posted Yet</h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-2 mb-6">Post a job to start seeing matched candidates.</p>
                    <Link to="/jobpost" className="bg-blue-600 text-white font-semibold px-6 py-2 rounded-lg hover:bg-blue-700 transition">
                        Post a Job
                    </Link>
                </div>
            </>
        );
    }

    return (
        <>
            <RecHomeNavbar />
            <div className="mt-14 p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">Matched Candidates</h1>
                {matches.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {matches.map(match => (
                            <div key={match.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100">{match.user_name}</h3>
                                    <div className="flex items-center gap-2 text-green-600 font-bold">
                                        <PercentIcon className="h-5 w-5" />
                                        <span>{(match.confidence_score * 100).toFixed(0)}%</span>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Match for: <span className="font-medium text-gray-800 dark:text-gray-200">{match.job_title}</span></p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">at <span className="font-medium">{match.job_company}</span></p>
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 flex justify-end">
                                    <Link to={`/candidate/${match.user}`} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-semibold text-sm">
                                        View Profile
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-center text-gray-500 dark:text-gray-400 mt-10">No matches found for your current job postings.</p>
                )}
            </div>
        </>
    );
}